import React, { useContext, useEffect, useRef, useState } from 'react'
import { LangContext } from '../../context/lang-context'
import { LoaderContext } from '../../context/loader-context'
import axios from 'axios'
import IndexedDBAsync from '../../db/IndexedDBAsync'
import schema from '../../util/schema.json'
import IndexedDB from '../../db/IndexedDB'

import { NotificationManager } from 'react-notifications'
import ReCAPTCHA from 'react-google-recaptcha'

import logo from '../../assets/images/logo-retail24.png'
import { API_URL, SHOP_URL } from '../../config/config'
import Loader from '../../components/Loader/Loader'
import moment from 'moment'
import useForm from '../../hooks/form-hook'
import MachinesFilterForm from './MachinesFilter'
import useAuth from '../../hooks/auth-hook'
import parseCookies from '../../util/parseCookies'
import setCookies from '../../util/setCookies'

const MobileLogin = ({ login, setPermission }) => {
    const shouldUseRecaptcha =
        window.location.hostname !== 'localhost' &&
        process.env.REACT_APP_LOCAL_DB === 'false'

    const loginTokenRef = useRef(null)
    const loginClientIdRef = useRef(null)

    const {
        loader,
        incrementRequests,
        decrementRequests
    } = useContext(LoaderContext)

    const [token, setToken] = useState('')
    const { TRL_Pack } = useContext(LangContext)
    const [isVerified, setIsVerified] = useState(false)

    const [blockedTime] = useState({
        start: null,
        end: null,
        duration: null
    })

    const [text, setText] = useState('')
    const [clientId] = useState()
    const [machinesCountOption, setMachinesCountOption] = useState()
    const [summaryMachineCount, setSummaryMachineCount] = useState()

    const [selectedMachinesCount, setSelectedMachinesCount] = useState(() => {
        let lastChosenMachinesCount

        try {
            lastChosenMachinesCount =
                parseCookies('loginFilter').LastChosenMachinesCount
        } catch (e) {}

        return lastChosenMachinesCount
    })

    const { form, openForm, closeForm } = useForm()
    const { logout } = useAuth()

    useEffect(() => {
        const db = new IndexedDB('dbV1', schema.schemas, 1)
        db.openDB()
    }, [])

    useEffect(() => {
        const options = []

        for (let i = 1; i <= 25; i++) {
            options.push(i * 10)
        }

        setMachinesCountOption(options)
        sessionStorage.setItem('isMobile', 'true')

        const metaDescription =
            document.querySelector('meta[name="description"]')

        let originalContent = ''

        if (metaDescription) {
            originalContent =
                metaDescription.getAttribute('content') || ''

            metaDescription.setAttribute(
                'content',
                'Portal R24 - wersja przeznaczona na przeglądarki mobilne'
            )
        }

        return () => {
            if (metaDescription && originalContent) {
                metaDescription.setAttribute(
                    'content',
                    originalContent
                )
            }
        }
    }, [])

    const getAuthConfig = () => {
        const authToken = loginTokenRef.current
        const normalizedClientId = loginClientIdRef.current

        if (!authToken) {
            throw new Error('Brak tokena logowania')
        }

        if (!normalizedClientId) {
            throw new Error('Brak ID klienta')
        }

        return {
            headers: {
                Authorization: `Bearer ${authToken}`,
                'X-Client-Id': normalizedClientId
            }
        }
    }

    const writeIntoDBSummary = async count => {
        try {
            const aDb = new IndexedDBAsync(
                'dbV1',
                schema.schemas,
                1
            )

            await aDb.openDB()

            await aDb.addOrPutItem(
                'otherInfo',
                {
                    count,
                    Type: 'machinesCount'
                },
                'machinesCount'
            )
        } catch (e) {
            console.error(e)
        }

        return count
    }

    const closeFormWithSaving = value => {
        if (typeof value === 'string') {
            try {
                JSON.parse(value)
            } catch (e) {
                console.log('can`t parse value')
                closeForm()
                return
            }

            setCookies('loginFilter', value)
        } else {
            let stringifyValue

            try {
                stringifyValue = JSON.stringify(value)
            } catch (e) {
                console.log('can`t parse value')
                closeForm()
                return
            }

            setCookies('loginFilter', stringifyValue)
        }

        logout()
            .then(() => setIsVerified(false))
            .then(() => closeForm())
    }

    const grecaptchaObject = window.grecaptcha

    const getMachineCount = () =>
        axios
            .get(
                `${API_URL}/api/mobile/machines-count`,
                getAuthConfig()
            )
            .then(res => {
                const machinesCount =
                    res?.data?.MachinesCount || 0

                setSummaryMachineCount(machinesCount)

                return machinesCount
            })

    const commitLogin = async clientIdValue => {
        const normalizedClientId = String(
            clientIdValue || ''
        )
            .trim()
            .toLowerCase()

        const authToken = loginTokenRef.current

        if (!authToken) {
            throw new Error('Brak tokena logowania')
        }

        if (!normalizedClientId) {
            throw new Error('Brak ID klienta')
        }

        const { data } = await axios.get(
            `${API_URL}/api/user-permissions`,
            {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'X-Client-Id': normalizedClientId
                }
            }
        )

        const permissions = data || []

        setPermission(permissions)

        sessionStorage.setItem(
            'permissions',
            JSON.stringify(permissions)
        )

        login(authToken, normalizedClientId)

        return permissions
    }

    const machinesPromise = () =>
        axios
            .post(
                `${API_URL}/api/mobile/machines`,
                {},
                getAuthConfig()
            )
            .then(response => ({    
                Machines: response.data
            }))

    const machineProductsPromise = () =>
        axios
            .post(
                `${API_URL}/api/mobile/machine-products`,
                {},
                getAuthConfig()
            )
            .then(response => ({
                MachineProduct: response.data
            }))

    const removePrevFilters = () =>
        localStorage.removeItem('loginFilter')

    const removePrevRecords = async (removeFilters = false) => {
        if (removeFilters) removePrevFilters()

        const dbAsync = new IndexedDBAsync('dbV1', schema.schemas, 1)
        await dbAsync.openDB()

        const transaction = dbAsync.db.transaction(
            ['machines', 'visits', 'machineProduct'],
            'readwrite'
        )

        await Promise.all([
            transaction.objectStore('machines').clear(),
            transaction.objectStore('visits').clear(),
            transaction.objectStore('machineProduct').clear()
        ])

        await transaction.done
    }

    const selectMachineCount = evt => {
        const count = Number(evt.target.value)
        let machinesFilter

        setSelectedMachinesCount(count)

        try {
            machinesFilter =
                parseCookies('loginFilter')
        } catch (e) {
            console.error(e)
            machinesFilter = {}
        }

        setCookies(
            'loginFilter',
            JSON.stringify({
                ...machinesFilter,
                LastChosenMachinesCount: count
            })
        )
    }

    const getNonFilteredInfo = async () => {
        incrementRequests()

        try {
            const value = await Promise.all([
                machinesPromise(),
                machineProductsPromise()
            ])

            const dbAsync = new IndexedDBAsync(
                'dbV1',
                schema.schemas,
                1
            )

            await dbAsync.openDB()

            let machines = []

            for (const item of value) {
                const key = Object.keys(item)[0]

                if (key === 'Machines') {
                    machines = item[key] || []

                    for (const machine of machines) {
                        await dbAsync.addOrPutItem(
                            'machines',
                            {
                                ...machine,
                                createDateTime: Date.now()
                            },
                            machine.MachineId
                        )
                    }
                }

                if (key === 'MachineProduct') {
                    const machineProducts =
                        item[key] || []

                    for (const machineProduct of machineProducts) {
                        await dbAsync.addOrPutItem(
                            'machineProduct',
                            {
                                ...machineProduct,
                                createDateTime: Date.now()
                            },
                            machineProduct.MachineProductId
                        )
                    }
                }
            }

            return machines.map(
                machine => machine.MachineId
            )
        } finally {
            decrementRequests()
        }
    }

    const handleSubmit = async evt => {
        evt.preventDefault()

        const {
            email,
            password,
            clientId,
            machineCountSelect
        } = evt.target.elements

        const emailValue = String(
            email.value || ''
        )
            .trim()
            .toLowerCase()

        const clientIdValue = String(
            clientId.value || ''
        )
            .trim()
            .toLowerCase()

        const selectedCount = Number(
            machineCountSelect.value ||
            selectedMachinesCount ||
            10
        )

        setSelectedMachinesCount(selectedCount)

        if (
            !emailValue ||
            !password.value ||
            !clientIdValue
        ) {
            return
        }

        incrementRequests()

        try {
            const res = await axios.put(
                `${API_URL}/api/auth/login`,
                {
                    Email: emailValue,
                    Password: password.value,
                    ClientId: clientIdValue,
                    token
                }
            )

            const authToken = res?.data?.token

            if (!authToken) {
                throw new Error(
                    'Backend nie zwrócił tokena'
                )
            }

            loginTokenRef.current = authToken
            loginClientIdRef.current = clientIdValue

            localStorage.setItem(
                'lastLogin',
                JSON.stringify({
                    email: emailValue,
                    clientId: clientIdValue
                })
            )

            setToken('')

            if (
                shouldUseRecaptcha &&
                grecaptchaObject
            ) {
                grecaptchaObject.reset()

                if (
                    res.headers['Origin'] ===
                        'http://localhost:3001' ||
                    res.headers['Origin'] ===
                        'http://localhost:3000' ||
                    res.headers[
                        'Access-Control-Allow-Origin'
                    ] ===
                        'https://portal.retail24.pl'
                ) {
                    axios.defaults.baseURL =
                        res.headers['Origin']
                }

                setIsVerified(true)
            }

            await removePrevRecords(
                sessionStorage.getItem(
                    'lastCustomerId'
                ) !== clientIdValue
            )

            sessionStorage.setItem(
                'lastCustomerId',
                clientIdValue
            )

            const machineCount =
                await getMachineCount()

            await writeIntoDBSummary(machineCount)

            if (machineCount >= selectedCount) {
                openForm()()
                return
            }

            await getNonFilteredInfo()
            await commitLogin(clientIdValue)
        } catch (e) {
            const err = e?.response

            if (e.message === 'Network Error') {
                setText(
                    'Podczas logowania wystąpił problem. Prosimy o zgłoszenie do serwisu'
                )

                setTimeout(() => {
                    setText('')
                }, 20000)
            } else if (err?.status === 429) {
                const duration =
                    err?.data?.message?.blockDuration

                const message =
                    err?.data?.message?.msg

                if (
                    Object.values(blockedTime).some(
                        value => !value
                    ) &&
                    duration
                ) {
                    const d = moment.duration(duration)

                    setText(
                        `Ilość prób logowania wyczerpana, spróbuj ponownie za ${
                            d.minutes()
                                ? `${d.minutes()} minuty`
                                : ''
                        } ${d.seconds()} sekund`
                    )

                    setTimeout(() => {
                        setText('')
                    }, duration)
                }

                if (message) {
                    NotificationManager.error(message)
                }
            } else if (err?.status > 299) {
                NotificationManager.error(
                    Object.values(
                        err?.data || {}
                    )[0] || 'Błąd logowania'
                )
            } else {
                NotificationManager.error(
                    e?.message ||
                    'Nie udało się zalogować'
                )
            }

            loginTokenRef.current = null
            loginClientIdRef.current = null

            setIsVerified(false)

            if (
                shouldUseRecaptcha &&
                grecaptchaObject
            ) {
                grecaptchaObject.reset()
            }

            console.error(e)
        } finally {
            sessionStorage.setItem(
                'isMobile',
                'true'
            )

            decrementRequests()
        }
    }

    const handleVerify = value => {
        if (value) {
            setToken(value)
            setIsVerified(true)
        }
    }

    return (
        <>
            {loader && <Loader />}

            <div className="col col-md-6 col-lg-5 mx-auto d-flex align-items-center">
                <div className="container">
                    {process.env.REACT_APP_LOCAL_DB ===
                        'true' && (
                        <a href={SHOP_URL}>Wróć</a>
                    )}

                    {text && (
                        <div
                            className="w-100 d-flex align-items-center"
                            style={{
                                background: '#EFE282',
                                height: '50px',
                                paddingLeft: '5px'
                            }}
                        >
                            <b>{text}</b>
                        </div>
                    )}

                    <div className="card card-body bg-fade mb-4">
                        <div className="text-center mt-2 mb-1">
                            <img
                                src={logo}
                                alt="logo"
                                height="80"
                            />
                        </div>

                        <div className="text-center mb-3">
                            <span
                                style={{
                                    fontSize: '1.5rem',
                                    background: '#FF7A00',
                                    paddingLeft: '2%',
                                    paddingRight: '2%',
                                    paddingTop: '.5%',
                                    paddingBottom: '.5%',
                                    fontFamily: 'Poppins',
                                    fontWeight: 'bold',
                                    color: 'black'
                                }}
                                className="badge"
                            >
                                mobile
                            </span>
                        </div>

                        <form
                            onSubmit={handleSubmit}
                            autoComplete="off"
                        >
                            <div className="form-group">
                                <label className="small">
                                    {TRL_Pack.auth.email}
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="small">
                                    {TRL_Pack.auth.password}
                                </label>

                                <input
                                    type="password"
                                    name="password"
                                    defaultValue=""
                                    autoComplete="off"
                                    className="form-control"
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="form-group">
                                <label className="small">
                                    {TRL_Pack.auth.clientId}
                                </label>

                                <input
                                    type="text"
                                    name="clientId"
                                    className="form-control"
                                    defaultValue={
                                        process.env.REACT_APP_LOCAL_DB ===
                                        'true'
                                            ? clientId
                                            : ''
                                    }
                                    disabled={
                                        process.env.REACT_APP_LOCAL_DB ===
                                        'true'
                                    }
                                    autoCapitalize="none"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label
                                    htmlFor="machineCountSelect"
                                    className="small"
                                >
                                    Liczba ładowanych automatów
                                </label>

                                <select
                                    className="form-control form-control-sm mt-0"
                                    name="machineCountSelect"
                                    id="machineCountSelect"
                                    value={
                                        selectedMachinesCount ||
                                        10
                                    }
                                    onChange={selectMachineCount}
                                >
                                    {machinesCountOption &&
                                        machinesCountOption.map(
                                            option => (
                                                <option
                                                    key={option}
                                                    value={option}
                                                >
                                                    {option}
                                                </option>
                                            )
                                        )}
                                </select>
                            </div>

                            {shouldUseRecaptcha ? (
                                <div className="mb-2 d-flex justify-content-center">
                                    <ReCAPTCHA
                                        sitekey="6LfIxKMpAAAAALIFN0aeEXcoxT0fUbQzq_3-0rjS"
                                        onChange={handleVerify}
                                    />
                                </div>
                            ) : null}

                            <button
                                className="btn btn-success btn-block"
                                disabled={
                                    shouldUseRecaptcha
                                        ? !isVerified
                                        : false
                                }
                            >
                                {TRL_Pack.buttons.login}
                            </button>
                        </form>
                    </div>
                </div>

                {form && (
                    <MachinesFilterForm
                        closeForm={closeFormWithSaving}
                        disabledExitByClickingOutside={true}
                        handleClose={closeFormWithSaving}
                        machinesCount={
                            summaryMachineCount
                        }
                        selectedMachinesCount={
                            selectedMachinesCount
                        }
                        commitLogin={commitLogin}
                    />
                )}
            </div>
        </>
    )
}

export default MobileLogin